import { Component } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { AuthService, BreadcrumbService, PartnershipService, StateService } from 'src/app/services';
import { Router } from '@angular/router';
import { IdentityWebApiClient, IPartnership, ListPartnershipsRequest, PartnershipStatus, UpdatePartnershipRequest } from 'identity-webapi-client';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ViewControls } from 'src/app/shared/models/view-controls';

@Component({
  selector: 'partnerships-list',
  templateUrl: './partnerships-list.component.html',
  styleUrls: ['./partnerships-list.component.less']
})

export class PartnershipsListComponent extends SituBaseComponent {
  viewControls = {
    pageIndex: 0,
    pageSize: 25,
    totalItems: 0,
    pageSizeOptions: [25, 50, 100],
    displayedColumns: ['partner', 'partneredSince', 'cta']
  } as ViewControls;

  enabledOnly: boolean = true;
  previousSearchTerm = "";
  searchTerm = "";
  partnershipsDataSource = new MatTableDataSource<IPartnership>();
  partnerships: IPartnership[] | undefined;
  actionablePartnerships: IPartnership[] | undefined;
  terminatedPartnerships: IPartnership[] | undefined;
  selectedPartnershipId: string | undefined;
  partnershipStatuses: PartnershipStatus | undefined;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private identityWebApiClient: IdentityWebApiClient,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar,
    private partnershipService: PartnershipService) {
      super(authService, stateService);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  protected onSelectedCompanyChange(): void {
    this.loadPartnerships();
    this.loadActionablePartnerships();
    this.loadTerminatedPartnerships();
  }

  loadPartnerships() {
    this.loading = true;

    let request = new ListPartnershipsRequest();

    if (this.searchTermChanged()) this.resetSearch();
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    request.searchTerm = this.searchTerm;
    request.companyId = this.selectedCompany?.id;
    request.partnershipStatuses = [PartnershipStatus.Approved, PartnershipStatus.Reconnected];
    this.previousSearchTerm = this.searchTerm;

    return this.partnershipService.ListPartnerships(request)
      .then(response => {
        this.partnerships = response.partnerships;
        this.partnershipsDataSource.data = response.partnerships ?? [];
        this.viewControls.totalItems = response.totalItems ?? 0;
      })
      .catch((err: any) => {})
      .finally(() => { this.loading = false; });
  }

  onEditPartnership(partnershipId: string) {
    this.router.navigate([`/admin/partnerships/${partnershipId}`]);
  }

  onClickAddPartnership(){
    this.router.navigate(["/admin/partnerships/new"]);
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    this.loadPartnerships();
  }

  resetSearch() {
    this.viewControls.pageIndex = 0;
    this.viewControls.pageSize = 25;
    this.viewControls.totalItems = 0;
  }

  getCompanyName(partnership: IPartnership): string | undefined {
    if (partnership.requesterCompanyId === this.selectedCompany?.id) {
      return partnership.requesteeCompany?.name;
    }
    return partnership.requesterCompany?.name;
  }

  transitionPartnership(transitionPartnershipRequest: TransitionPartnershipRequest) {
    this.loading = true;
    this.selectedPartnershipId = transitionPartnershipRequest.partnership.id;

    let request = new UpdatePartnershipRequest();
    request.id = transitionPartnershipRequest.partnership.id;
    request.status = transitionPartnershipRequest.newStatus;
    request.companyId = this.selectedCompany?.id;

    return this.identityWebApiClient.updatePartnership(request)
      .then(response => {
        if (response.success) {
          switch (request.status) {
            case PartnershipStatus.Cancelled:
              this.loadActionablePartnerships();
              break;
            case PartnershipStatus.Terminated:
              this.loadPartnerships();
              this.loadTerminatedPartnerships();
              break;
            case PartnershipStatus.Reconnected:
              this.loadPartnerships();
              this.loadTerminatedPartnerships();
              break;
          };
        } else {
          this.snackBar.open(`Error: ${response.errorMessages}!`, 'OK', { duration: 2000 });
        }
        this.snackBar.open(`Partnership with ${this.partnershipService.getOtherCompanyName(transitionPartnershipRequest.partnership, this.selectedCompany?.id ?? "")} ${PartnershipStatus[transitionPartnershipRequest.newStatus]}!`, 'OK', { duration: 2000 });
        this.loading = false;
        this.selectedPartnershipId = undefined;
      }, error => {
        this.loading = false;
        this.selectedPartnershipId = undefined;
      })
      .catch(err => { 
        this.snackBar.open(`Error updating Partnership status!`, 'OK', { duration: 2000 } );
        this.loading = false;
        this.selectedPartnershipId = undefined;
      });
  }

  private searchTermChanged(): boolean {
    return this.searchTerm.toLowerCase() !== this.previousSearchTerm.toLowerCase();
  }

  private loadActionablePartnerships() {
    this.loading = true;

    let request = new ListPartnershipsRequest();

    request.pageNumber = 1;
    request.pageSize = 200;
    request.companyId = this.selectedCompany?.id;
    request.partnershipStatuses = [PartnershipStatus.Requested];

    return this.partnershipService.ListPartnerships(request)
      .then(response => {
        if (response.success && response.partnerships && response.partnerships.length > 0) {
          this.actionablePartnerships = response.partnerships ?? undefined;
        } else {
          this.actionablePartnerships = undefined;
        }
      }, error => {
        this.loading = false;
      });
  }
  
  private loadTerminatedPartnerships() {
    this.loading = true;

    let request = new ListPartnershipsRequest();

    request.pageNumber = 1;
    request.pageSize = 200;
    request.companyId = this.selectedCompany?.id;
    request.partnershipStatuses = [PartnershipStatus.Terminated];

    return this.partnershipService.ListPartnerships(request)
      .then(response => {
        if (response.success && response.partnerships && response.partnerships.length > 0) {
          this.terminatedPartnerships = response.partnerships ?? undefined;
        } else {
          this.terminatedPartnerships = undefined;
        }
      }, error => {
        this.loading = false;
      });
  }
}

export interface TransitionPartnershipRequest {
  partnership: IPartnership;
  newStatus: PartnershipStatus;
}
